import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { firstValueFrom, Observable } from 'rxjs';
import { Injectable } from '@angular/core';

export interface IStateService<T> {
  readonly apiUrl: string;

  deleteMany(data: T[]): Observable<T[]>;

  deleteManyAsync(data: T[]): Promise<T[]>;

  deleteOne(data: T): Observable<T>;

  deleteOneAsync(data: T): Promise<T>;

  getAll(): Observable<T[]>;

  getAllAsync(): Promise<T[]>;

  getMany(): Observable<T[]>;

  getManyAsync(): Promise<T[]>;

  getOne(): Observable<T>;

  getOneAsync(): Promise<T>;

  getOneById(id: number): Observable<T>;

  getOneByIdAsync(id: number): Promise<T>;

  patchMany(data: T[]): Observable<T[]>;

  patchManyAsync(data: T[]): Promise<T[]>;

  patchOne(data: T): Observable<T>;

  patchOneAsync(data: T): Promise<T>;

  postMany(data: T[]): Observable<T[]>;

  postManyAsync(data: T[]): Promise<T[]>;

  postOne(data: T): Observable<T>;

  postOneAsync(data: T): Promise<T>;

  putMany(data: T[]): Observable<T[]>;

  putManyAsync(data: T[]): Promise<T[]>;

  putOne(data: T): Observable<T>;

  putOneAsync(data: T): Promise<T>;
}

/**
 * State Service Abstract
 */
@Injectable({
  providedIn: 'root'
})
export abstract class StateService<T> {

  /**
   * Represents the base URL of the API endpoint that the application will interact with.
   *
   * This URL is used as the foundation for all API requests, ensuring that each request
   * is directed to the appropriate server. It should include the protocol (e.g., http or https)
   * and the domain name, and it may also include a specific port and base path.
   *
   * Typical usage scenarios for this variable include:
   *   - Fetching data from the server via HTTP GET requests.
   *   - Sending data to the server via HTTP POST requests.
   *   - Updating server data via HTTP PUT or PATCH requests.
   *   - Deleting data from the server via HTTP DELETE requests.
   *
   * This variable should be treated as a constant throughout the application's lifecycle,
   * and it's generally set during the application's initialization phase. Changing it at
   * runtime could lead to unexpected results and should be avoided.
   */
  public abstract apiUrl: string;
  /**
   * The base URL for API requests.
   *
   * This variable contains the base URL retrieved from the environment
   * configuration, typically pointing to the API's root endpoint.
   *
   * @type {string}
   */
  private baseUrl: string = environment.apiUrl;

  /**
   * Constructor for a class that requires an HttpClient.
   *
   * @param {HttpClient} http - The HTTP client to be used for making HTTP requests.
   */
  protected constructor(private http: HttpClient) {
  }

  /**
   * Retrieves many items of type T as an Observable array.
   *
   * @return {Observable<T[]>} An Observable that emits an array of items of type T.
   */
  public abstract getAll(): Observable<T[]>;

  /**
   * Retrieves many items of type T as an Observable array.
   *
   * @return {Observable<T[]>} An Observable that emits an array of items of type T.
   */
  public abstract getMany(): Observable<T[]>;

  /**
   * Fetches a single entity.
   *
   * @return {Observable<T>} An observable that emits a single entity of type T.
   */
  public abstract getOne(): Observable<T>;

  /**
   * Retrieves a single entity by its unique identifier.
   *
   * @param {number} id - The unique identifier of the entity to be retrieved.
   * @return {Observable<T>} An Observable that emits the entity identified by the id, or an error if the entity is not found.
   */
  public abstract getOneById(id: number): Observable<T>;

  /**
   * Sends a POST request to create a new resource.
   *
   * @param {T} data - The data to be sent in the request body.
   * @return {Observable<T>} An observable containing the created resource.
   */
  public abstract postOne(data: T): Observable<T>;

  /**
   * Sends an array of data items to the server.
   *
   * @param {T[]} data - An array of data items to be posted.
   * @return {Observable<T[]>} - An observable containing the posted data items.
   */
  public abstract postMany(data: T[]): Observable<T[]>;

  /**
   * Updates or patches an existing resource with the given data.
   * @param {T} data - The data to update or patch the resource with.
   * @return {Observable<T>} An observable that emits the updated resource.
   */
  public abstract patchOne(data: T): Observable<T>;

  /**
   * Patches multiple records with the given data and returns an observable of the patched records.
   *
   * @param {T[]} data - An array of records to be patched.
   * @return {Observable<T[]>} - An observable that emits the patched records.
   */
  public abstract patchMany(data: T[]): Observable<T[]>;

  /**
   * Inserts a single entity into the data source.
   *
   * @param {T} data - The entity to be inserted.
   * @return {Observable<T>} An observable that emits the inserted entity.
   */
  public abstract putOne(data: T): Observable<T>;

  /**
   * Method for adding multiple data items to a data store.
   *
   * @param {T[]} data - An array of data items to be added.
   * @return {Observable<T[]>} An observable that emits the added data items.
   */
  public abstract putMany(data: T[]): Observable<T[]>;

  /**
   * Deletes a single entity of type T.
   *
   * @param {T} data - The entity to be deleted.
   * @return {Observable<T>} An Observable that emits the deleted entity.
   */
  public abstract deleteOne(data: T): Observable<T>;

  /**
   * Deletes multiple entities from the data source.
   *
   * @param {T[]} data - An array of entities to be deleted.
   * @return {Observable<T[]>} An observable that emits the deleted entities.
   */
  public abstract deleteMany(data: T[]): Observable<T[]>;

  /**
   * Fetches all the items asynchronously.
   *
   * @return {Promise<T[]>} A promise that resolves to an array of items.
   */
  public abstract getAllAsync(): Promise<T[]>;

  /**
   * Retrieves multiple items asynchronously.
   *
   * @return {Promise<T[]>} A promise that resolves to an array of items.
   */
  public abstract getManyAsync(): Promise<T[]>;

  /**
   * Asynchronously retrieves a single entity.
   *
   * @return {Promise<T>} A promise that resolves to the entity of type T.
   */
  public abstract getOneAsync(): Promise<T>;

  /**
   * Retrieves a single entity based on its unique identifier.
   *
   * @param {number} id - The unique identifier of the entity to be retrieved.
   * @return {Promise<T>} A promise that resolves to the entity object if found, or null if not found.
   */
  public abstract getOneByIdAsync(id: number): Promise<T>;

  /**
   * Creates a new entry asynchronously.
   *
   * @param {T} data - The data to be posted.
   * @return {Promise<T>} A promise that resolves with the created entry.
   */
  public abstract postOneAsync(data: T): Promise<T>;

  /**
   * Posts an array of data asynchronously.
   *
   * @param data - An array of data items of type T to be posted.
   * @return A promise that resolves to an array of posted data items of type T.
   */
  public abstract postManyAsync(data: T[]): Promise<T[]>;

  /**
   * Asynchronously applies a patch to a single record of type T.
   *
   * @param {T} data - The data object that contains the patch information.
   * @return {Promise<T>} A promise that resolves to the updated record of type T.
   */
  public abstract patchOneAsync(data: T): Promise<T>;

  /**
   * Patches multiple records asynchronously.
   *
   * @param {T[]} data - An array of records to be patched.
   * @return {Promise<T[]>} A promise that resolves with an array of patched records.
   */
  public abstract patchManyAsync(data: T[]): Promise<T[]>;

  /**
   * Asynchronously inserts a single item into the data store.
   *
   * @param {T} data - The item to be inserted.
   * @return {Promise<T>} A promise that resolves to the inserted item.
   */
  public abstract putOneAsync(data: T): Promise<T>;

  /**
   * Asynchronously adds multiple items to the underlying storage or data structure.
   *
   * @param {T[]} data - An array of items to be added.
   * @return {Promise<T[]>} - A promise that resolves with the array of items that were added.
   */
  public abstract putManyAsync(data: T[]): Promise<T[]>;

  /**
   * Asynchronously deletes a single record from the data source.
   *
   * @param {T} data - The data record to be deleted.
   * @return {Promise<T>} A promise that resolves to the deleted data record.
   */
  public abstract deleteOneAsync(data: T): Promise<T>;

  /**
   * Deletes multiple records from the data source asynchronously.
   *
   * @param {T[]} data - An array of records to be deleted.
   * @return {Promise<T[]>} A promise that resolves to an array of deleted records.
   */
  public abstract deleteManyAsync(data: T[]): Promise<T[]>;

  /**
   * Makes an HTTP GET request to the specified URL and returns an observable.
   *
   * @param {string} url - The endpoint URL to which the GET request is sent.
   * @return {Observable<T[]>} An observable that emits an array of the requested resource type.
   */
  protected get(url: string = this.apiUrl): Observable<T[]> {
    return this.http.get<T[]>(this.baseUrl + url);
  }

  /**
   * Makes an HTTP GET request to the specified URL and returns an Observable of an array of the generic type K.
   *
   * @param url - The endpoint URL to make the GET request to.
   * @return An Observable that emits an array of the generic type K.
   */
  protected getGeneric<K>(url: string = this.apiUrl): Observable<K[]> {
    return this.http.get<K[]>(this.baseUrl + url);
  }

  /**
   * Retrieves data asynchronously from the given URL.
   *
   * @param {string} url The URL from which to fetch data.
   * @return {Promise<T[]>} A promise that resolves to an array of type T.
   */
  protected getAsync(url: string = this.apiUrl): Promise<T[]> {
    return firstValueFrom(this.get(url));
  }

  /**
   * Fetches data from the specified URL and returns a promise of an array of the specified generic type.
   *
   * @param {string} url - The URL from which to fetch the data.
   * @return {Promise<K[]>} A promise that resolves to an array of the type K.
   */
  protected getGenericAsync<K>(url: string = this.apiUrl): Promise<K[]> {
    return firstValueFrom(this.getGeneric<K>(url));
  }

  /**
   * Retrieves an entity by its unique identifier.
   *
   * @param {string} url - The endpoint URL to fetch data from.
   * @param {number} id - The unique identifier of the entity to be retrieved.
   * @return {Observable<T>} An observable containing the requested entity.
   */
  protected getById(id: number, url: string = this.apiUrl): Observable<T> {
    return this.http.get<T>(this.baseUrl + url + "/" + id);
  }

  /**
   * Fetches an item by its ID from a specified URL.
   *
   * @param {string} url - The endpoint URL to fetch the item from.
   * @param {number} id - The unique identifier of the item to be fetched.
   * @return {Observable<K>} An observable containing the requested item.
   */
  protected getByIdGeneric<K>(id: number, url: string = this.apiUrl): Observable<K> {
    return this.http.get<K>(this.baseUrl + url + "/" + id);
  }

  /**
   * Retrieves a resource by its ID asynchronously.
   *
   * @param {string} url - The endpoint URL for the resource.
   * @param {number} id - The unique identifier of the resource.
   * @return {Promise<T>} A promise that resolves to the resource object of type T.
   */
  protected getByIdAsync(id: number, url: string = this.apiUrl): Promise<T> {
    return firstValueFrom(this.getById(id, url));
  }

  /**
   * Fetches an entity by ID from a specified URL and returns a Promise resolving to the entity.
   *
   * @param {string} url - The endpoint URL from which to fetch the entity.
   * @param {number} id - The ID of the entity to fetch.
   * @return {Promise<K>} - A promise that resolves to the entity of type K.
   */
  protected getByIdGenericAsync<K>(id: number, url: string = this.apiUrl): Promise<K> {
    return firstValueFrom(this.getByIdGeneric<K>(id, url));
  }

  /**
   * Sends an HTTP POST request to the specified URL with the provided data.
   *
   * @param {string} url - The endpoint to which the POST request is sent.
   * @param {T} data - The payload to be sent in the body of the POST request.
   * @return {Observable<T>} An Observable that will emit the response from the server.
   */
  protected post(data: T, url: string = this.apiUrl): Observable<T> {
    return this.http.post<T>(this.baseUrl + url, data);
  }

  /**
   * Sends a POST request to the specified URL with the given data.
   *
   *
   * @param url - The endpoint to which the POST request is sent.
   * @param data - The data payload to be sent with the POST request.
   * @return An Observable that emits the response of the POST request.
   */
  protected postGeneric<K>(data: K, url: string = this.apiUrl): Observable<K> {
    return this.http.post<K>(this.baseUrl + url, data);
  }

  /**
   * Sends a POST request asynchronously to the specified URL with the given data.
   *
   * @param {string} url - The URL to which the POST request is made.
   * @param {T} data - The data to be sent in the body of the POST request.
   * @return {Promise<T>} - A promise that resolves to the response of the POST request.
   */
  protected postAsync(data: T, url: string = this.apiUrl): Promise<T> {
    return firstValueFrom(this.post(data, url));
  }

  /**
   * Asynchronously posts data to the specified URL and returns a promise.
   *
   * @param url The endpoint URL to which the data should be posted.
   * @param data The data to be posted to the URL.
   * @return A promise that resolves with the posted data.
   */
  protected postGenericAsync<K>(data: K, url: string = this.apiUrl): Promise<K> {
    return firstValueFrom(this.postGeneric<K>(data, url));
  }

  /**
   * Sends a PATCH request to the specified URL with the given data.
   *
   * @param {string} url - The endpoint URL to which the PATCH request is sent.
   * @param {T} data - The data to be sent in the body of the PATCH request.
   * @return {Observable<T>} An observable containing the server's response.
   */
  protected patch(data: T, url: string = this.apiUrl): Observable<T> {
    return this.http.patch<T>(this.baseUrl + url, data);
  }

  /**
   * Sends an HTTP PATCH request to the specified URL with the provided data.
   *
   * @param {string} url - The endpoint to which the PATCH request is sent.
   * @param {K} data - The payload to be sent with the PATCH request.
   * @returns {Observable<K>} - An observable containing the server's response.
   */
  protected patchGeneric<K>(data: K, url: string = this.apiUrl): Observable<K> {
    return this.http.patch<K>(this.baseUrl + url, data);
  }

  /**
   * Sends an asynchronous PATCH request to the specified URL with the provided data.
   *
   * @param {string} url - The endpoint to which the PATCH request is sent.
   * @param {T} data - The data to be included in the body of the PATCH request.
   * @return {Promise<T>} - A Promise that resolves to the response data.
   */
  protected patchAsync(data: T, url: string = this.apiUrl): Promise<T> {
    return firstValueFrom(this.patch(data, url));
  }

  /**
   * Asynchronously patches data to the specified URL and returns a promise with the patched data.
   *
   * @param {string} url - The URL to which the data should be patched.
   * @param {K} data - The data to be patched.
   * @return {Promise<K>} A promise that resolves with the patched data.
   */
  protected patchGenericAsync<K>(data: K, url: string = this.apiUrl): Promise<K> {
    return firstValueFrom(this.patchGeneric<K>(data, url));
  }

  /**
   * Sends a PATCH request to the specified URL with the provided data for a resource identified by the given ID.
   *
   * @param {string} url - The endpoint URL to send the request to.
   * @param {number} id - The unique identifier of the resource to be updated.
   * @param {T} data - The data to update the resource with.
   * @return {Observable<T>} An observable that emits the response data.
   */
  protected patchById(id: number, data: T, url: string = this.apiUrl): Observable<T> {
    return this.http.patch<T>(this.baseUrl + url + "/" + id, data);
  }

  /**
   * Sends a PATCH request to update an existing resource identified by the given ID with the provided data.
   *
   * @param {string} url - The endpoint URL relative to the base URL.
   * @param {number} id - The unique identifier of the resource to be updated.
   * @param {K} data - The data to update the resource with.
   * @return {Observable<K>} - An observable containing the updated resource.
   */
  protected patchByIdGeneric<K>(id: number, data: K, url: string = this.apiUrl): Observable<K> {
    return this.http.patch<K>(this.baseUrl + url + "/" + id, data);
  }

  /**
   * Asynchronously sends a PATCH request to update a resource by its ID.
   *
   * @param {string} url - The endpoint URL where the request should be sent.
   * @param {number} id - The unique identifier of the resource to update.
   * @param {T} data - The data payload to send with the PATCH request.
   * @return {Promise<T>} - A promise that resolves to the updated resource.
   */
  protected patchByIdAsync(id: number, data: T, url: string = this.apiUrl): Promise<T> {
    return firstValueFrom(this.patchById(id, data, url));
  }

  /**
   * Asynchronously patches an entity identified by its ID with the provided data.
   *
   * @param {string} url - The endpoint to send the patch request to.
   * @param {number} id - The ID of the entity to be patched.
   * @param {K} data - The data to patch the entity with.
   * @return {Promise<K>} A promise that resolves to the patched entity.
   */
  protected patchByIdGenericAsync<K>(id: number, data: K, url: string = this.apiUrl): Promise<K> {
    return firstValueFrom(this.patchByIdGeneric<K>(id, data, url));
  }

  /**
   * Sends an HTTP PUT request to the specified URL with the given data.
   * @param {string} url - The endpoint URL to which the request is sent.
   * @param {T} data - The data to be sent in the body of the PUT request.
   * @return {Observable<T>} - An Observable that emits the response of the PUT request.
   */
  protected put(data: T, url: string = this.apiUrl): Observable<T> {
    return this.http.put<T>(this.baseUrl + url, data);
  }

  /**
   * Sends an HTTP PUT request to the specified URL with the provided data.
   *
   * @param {string} url - The endpoint to which the PUT request is sent.
   * @param {K} data - The data to be sent with the PUT request.
   * @return {Observable<K>} - An observable of the response with a generic type.
   */
  protected putGeneric<K>(data: K, url: string = this.apiUrl): Observable<K> {
    return this.http.put<K>(this.baseUrl + url, data);
  }

  /**
   * Sends an asynchronous HTTP PUT request to the specified URL with the provided data.
   * @param {string} url - The URL to which the PUT request is sent.
   * @param {T} data - The data to be sent with the PUT request.
   * @return {Promise<T>} A promise that resolves to the response of the PUT request.
   */
  protected putAsync(data: T, url: string = this.apiUrl): Promise<T> {
    return firstValueFrom(this.put(data, url));
  }

  /**
   * Asynchronously sends a PUT request to the specified URL with the given data.
   *
   * @param {string} url - The endpoint to which the PUT request is sent.
   * @param {K} data - The data to be sent in the PUT request.
   * @returns {Promise<K>} - A promise that resolves to the response data of type K.
   */
  protected putGenericAsync<K>(data: K, url: string = this.apiUrl): Promise<K> {
    return firstValueFrom(this.putGeneric<K>(data, url));
  }

  /**
   * Sends a PUT request to update a resource by its ID.
   *
   * @param {string} url - The endpoint URL to append to the base URL.
   * @param {number} id - The unique identifier of the resource to be updated.
   * @param {T} data - The data object containing the updates to be made.
   * @return {Observable<T>} - An observable that emits the updated resource.
   */
  protected putById(id: number, data: T, url: string = this.apiUrl): Observable<T> {
    return this.http.put<T>(this.baseUrl + url + "/" + id, data);
  }

  /**
   * Sends a PUT request to the specified URL with the given ID and data.
   *
   * @param {string} url - The endpoint URL where the request is sent.
   * @param {number} id - The unique identifier for the resource being updated.
   * @param {K} data - The data to update the resource with.
   * @return {Observable<K>} An observable containing the updated resource.
   */
  protected putByIdGeneric<K>(id: number, data: K, url: string = this.apiUrl): Observable<K> {
    return this.http.put<K>(this.baseUrl + url + "/" + id, data);
  }

  /**
   * Updates a resource by its ID asynchronously.
   *
   * @param {string} url - The endpoint URL to send the PUT request to.
   * @param {number} id - The unique identifier of the resource to be updated.
   * @param {T} data - The data to update the resource with.
   * @return {Promise<T>} - A promise that resolves to the updated resource.
   */
  protected putByIdAsync(id: number, data: T, url: string = this.apiUrl): Promise<T> {
    return firstValueFrom(this.putById(id, data, url));
  }

  /**
   * Asynchronously updates an item by its ID using the provided data and returns a Promise of the updated item.
   *
   * @param {string} url - The base URL for the API endpoint.
   * @param {number} id - The unique identifier of the item to be updated.
   * @param {K} data - The data to update the item with.
   * @return {Promise<K>} A promise that resolves to the updated item.
   */
  protected putByIdGenericAsync<K>(id: number, data: K, url: string = this.apiUrl): Promise<K> {
    return firstValueFrom(this.putByIdGeneric<K>(id, data, url));
  }

  /**
   * Performs a soft delete operation by sending an HTTP DELETE request to the specified URL.
   *
   * @param {string} url - The URL to which the DELETE request will be sent.
   * @return {Observable<T>} An observable that emits the response from the DELETE request.
   */
  protected softDelete(url: string = this.apiUrl): Observable<T> {
    return this.http.delete<T>(this.baseUrl + url);
  }

  /**
   * Soft deletes a resource identified by the given URL.
   *
   * @param {string} url - The URL of the resource to be soft deleted.
   * @return {Observable<K>} An observable of the deleted resource.
   */
  protected softDeleteGeneric<K>(url: string = this.apiUrl): Observable<K> {
    return this.http.delete<K>(this.baseUrl + url);
  }

  /**
   * Marks a resource as deleted without physically removing it.
   *
   * @param url The endpoint of the resource to be soft-deleted.
   * @return A promise that resolves to the result of the soft deletion.
   */
  protected softDeleteAsync(url: string = this.apiUrl): Promise<T> {
    return firstValueFrom(this.softDelete(url));
  }

  /**
   * Asynchronously performs a soft delete operation on the specified URL.
   *
   * @param {string} url The endpoint to perform the soft delete on.
   * @return {Promise<K>} A promise that resolves with the result of the soft delete operation.
   */
  protected softDeleteGenericAsync<K>(url: string = this.apiUrl): Promise<K> {
    return firstValueFrom(this.softDeleteGeneric<K>(url));
  }

  /**
   * Performs a soft delete operation for a specified resource identified by its ID.
   *
   * @param {string} url - The endpoint URL relative to the base URL.
   * @param {number} id - The unique identifier of the resource to be soft deleted.
   * @return {Observable<T>} An observable containing the server's response.
   */
  protected softDeleteById(id: number, url: string = this.apiUrl): Observable<T> {
    return this.http.delete<T>(this.baseUrl + url + "/" + id);
  }

  /**
   * Soft deletes a resource by its ID through an HTTP DELETE request.
   *
   * @param url The endpoint URL to which the DELETE request should be made.
   * @param id The ID of the resource to be soft deleted.
   * @return An Observable of the generic type K, indicating the result of the delete operation.
   */
  protected softDeleteByIdGeneric<K>(id: number, url: string = this.apiUrl): Observable<K> {
    return this.http.delete<K>(this.baseUrl + url + "/" + id);
  }

  /**
   * Soft deletes a record by its ID.
   * This method marks the record as deleted without permanently removing it from the database.
   *
   * @param {string} url - The endpoint URL for the soft delete operation.
   * @param {number} id - The ID of the record to be soft deleted.
   * @return {Promise<T>} - A promise that resolves to the response of the soft delete operation.
   */
  protected softDeleteByIdAsync(id: number, url: string = this.apiUrl): Promise<T> {
    return firstValueFrom(this.softDeleteById(id, url));
  }

  /**
   * Deletes a resource by its ID using a soft delete method.
   * This operation is performed asynchronously.
   *
   * @param {string} url - The endpoint URL for the deletion request.
   * @param {number} id - The unique identifier of the resource to be deleted.
   * @return {Promise<K>} A promise that resolves with the deleted resource or response.
   */
  protected softDeleteByIdGenericAsync<K>(id: number, url: string = this.apiUrl): Promise<K> {
    return firstValueFrom(this.softDeleteByIdGeneric<K>(id, url));
  }
}
